import React, { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Repeat, Plus, X, Pencil, Check } from "lucide-react";
import { fmtRoDate } from "../../lib/dateFmt";
import { todayIso, fmtDateTime } from "./utils";
import OwnerBadge from "./OwnerBadge";

export default function RoutinePanel({ scope }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const readOnly = scope === "all";

  const load = async () => {
    try {
      const { data } = await api.get(`/khreis-task/routine${scope === "all" ? "?scope=all" : ""}`);
      setItems(data.rows || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      await api.post("/khreis-task/routine", { text: t });
      setText("");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const toggleToday = async (it) => {
    const today = todayIso();
    const isDoneToday = it.last_done_date === today;
    try {
      await api.put(`/khreis-task/routine/${it.id}/done`, { date: isDoneToday ? null : today });
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (it) => {
    if (!window.confirm("Ștergi această rutină? Va dispărea complet.")) return;
    try {
      await api.delete(`/khreis-task/routine/${it.id}`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveEdit = async () => {
    const t = editText.trim();
    if (!t) return;
    try {
      await api.put(`/khreis-task/routine/${editingId}`, { text: t });
      setEditingId(null);
      setEditText("");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const todayDoneCount = items.filter((i) => i.last_done_date === todayIso()).length;

  return (
    <div className="card-flat p-4 sm:p-5" data-testid="panel-routine">
      <div className="flex items-center justify-between pb-3 border-b mb-3">
        <div className="flex items-center gap-2">
          <Repeat size={18} className="text-blue-600"/>
          <h2 className="font-display text-lg font-bold">TASK ROUTINE</h2>
          {readOnly && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">👁 Vizualizare globală</span>}
        </div>
        <div className="text-[10px] text-zinc-500">
          <strong className="text-emerald-700">{todayDoneCount}/{items.length}</strong> bifate azi
        </div>
      </div>

      {!readOnly && (
        <div className="flex items-center gap-2 mb-3">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="Adaugă o rutină permanentă… (Enter)"
            data-testid="routine-new-input"
          />
          <Button onClick={add} disabled={!text.trim() || busy} className="bg-blue-600 hover:bg-blue-700" data-testid="routine-add-btn">
            <Plus size={14}/>
          </Button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-xs text-zinc-400 italic py-6 text-center border border-dashed rounded">
          Nicio rutină. Adaugă una pentru a o avea zilnic!
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
          {items.map((it) => {
            const today = todayIso();
            const doneToday = it.last_done_date === today;
            const everDone = !!it.last_done_date;
            return (
              <div
                key={it.id}
                className={`flex items-center gap-2 px-3 py-2 rounded border group transition-colors ${doneToday ? "bg-emerald-50 border-emerald-300" : "bg-white border-blue-200 hover:border-blue-400"}`}
                data-testid={`routine-${it.id}`}
              >
                <input
                  type="checkbox"
                  checked={doneToday}
                  onChange={() => { if (!readOnly) toggleToday(it); }}
                  disabled={readOnly}
                  className="w-4 h-4 accent-emerald-600 cursor-pointer flex-shrink-0 disabled:cursor-not-allowed"
                  data-testid={`routine-check-${it.id}`}
                  title={doneToday ? "Click pentru a debifa pe azi" : "Click pentru a bifa pe azi"}
                />
                <div className="flex-1 min-w-0">
                  {editingId === it.id ? (
                    <div className="flex items-center gap-1">
                      <Input
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(); } else if (e.key === "Escape") setEditingId(null); }}
                        className="h-8 text-sm"
                        autoFocus
                      />
                      <Button onClick={saveEdit} size="sm" className="bg-blue-600 hover:bg-blue-700 h-8"><Check size={12}/></Button>
                      <Button onClick={() => setEditingId(null)} variant="outline" size="sm" className="h-8"><X size={12}/></Button>
                    </div>
                  ) : (
                    <>
                      <div className={`text-sm flex items-center gap-1.5 ${doneToday ? "line-through text-emerald-700" : "text-zinc-800"}`}>
                        <span className="flex-1">🔁 {it.text}</span>
                        {readOnly && <OwnerBadge item={it}/>}
                      </div>
                      <div className="text-[10px] text-zinc-400 mt-0.5">
                        Adăugat {fmtDateTime(it.created_at)}
                        {everDone && (
                          <span className={doneToday ? "text-emerald-700" : "text-blue-600"}>
                            {" "}· ✓ {doneToday ? "Rezolvat azi" : `Ultima rezolvare ${fmtRoDate(it.last_done_date)}`}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {editingId !== it.id && !readOnly && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => { setEditingId(it.id); setEditText(it.text); }}
                      className="text-zinc-500 hover:text-zinc-900 p-1"
                      title="Editează"
                      data-testid={`routine-edit-${it.id}`}
                    >
                      <Pencil size={12}/>
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(it)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Șterge rutină (definitiv)"
                      data-testid={`routine-delete-${it.id}`}
                    >
                      <X size={14}/>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
